import React from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import { CountryCodeSelector } from "../../../../components/CountrySelector/CountrySelector";
import { PhoneTextField } from "../../../../components/PhoneTextField/PhoneTextField";
import { FieldLabel } from "../components/FieldLabel";
import { ACCENT } from "../BusinessOnboard.constant";
import { ICountry } from "../../../../constants/countries";

interface IMobileStepProps {
  mobileNumber: string;
  selectedCountryIso: string;
  selectedCountryCode: string;
  mobileError: string | null;
  mobileExists: boolean;
  onCountryChange: (country: ICountry) => void;
  onMobileChange: (digits: string) => void;
  onLogin: () => void;
  onRecoverPassword: () => void;
  onBack: () => void;
}

/**
 * Step 0a — Mobile number entry. Purely presentational: the "Send verification
 * code" primary button lives in the shared StepFooter (owned by the container).
 * When the number already exists, shows Login / Recover CTAs instead.
 */
export const MobileStep: React.FC<IMobileStepProps> = ({
  mobileNumber,
  selectedCountryIso,
  selectedCountryCode,
  mobileError,
  mobileExists,
  onCountryChange,
  onMobileChange,
  onLogin,
  onRecoverPassword,
  onBack,
}) => {
  return (
    <Stack>
      <FieldLabel label="Mobile number" />
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ width: 118, flexShrink: 0 }}>
          <CountryCodeSelector
            value={selectedCountryIso}
            onChange={onCountryChange}
            sx={{
              "&.MuiOutlinedInput-root": {
                minHeight: 48,
                borderRadius: "10px",
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <PhoneTextField
            placeholder="Enter 10-digit number"
            value={mobileNumber.replace(selectedCountryCode, "")}
            onChange={(value) => {
              const digitsOnly = value.replace(/\D/g, "");
              if (digitsOnly.length <= 10) {
                onMobileChange(digitsOnly);
              }
            }}
            error={!!mobileError}
            helperText={mobileError || undefined}
            sx={{
              "& .MuiInputBase-root": {
                minHeight: 48,
                borderRadius: "10px",
              },
            }}
          />
        </Box>
      </Box>

      {mobileExists && (
        <Stack spacing={1.5} mt={2.5}>
          <Typography
            sx={{
              fontSize: "0.9rem",
              color: "text.secondary",
              textAlign: "center",
            }}
          >
            Choose an option to continue:
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={onBack}
              sx={{
                width: 46,
                height: 46,
                flexShrink: 0,
                border: "1px solid",
                borderColor: "grey.300",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "grey.400",
                  backgroundColor: "grey.50",
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Button
              fullWidth
              onClick={onLogin}
              sx={{
                py: 1.3,
                borderRadius: "12px",
                fontWeight: 700,
                textTransform: "none",
                color: "#fff",
                backgroundColor: ACCENT.main,
                boxShadow: "none",
                "&:hover": { backgroundColor: ACCENT.dark, boxShadow: "none" },
              }}
            >
              Login to Your Account
            </Button>
          </Box>
          <Button
            fullWidth
            variant="outlined"
            onClick={onRecoverPassword}
            sx={{
              py: 1.3,
              borderRadius: "12px",
              fontWeight: 700,
              textTransform: "none",
              color: ACCENT.main,
              borderColor: ACCENT.main,
              "&:hover": { borderColor: ACCENT.dark, backgroundColor: ACCENT.soft },
            }}
          >
            Recover Your Password
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
